import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useMemo, useState } from 'react'
import {
  explainMatch,
  findRuleProblems,
  formatSla,
  normaliseKeywords,
  validateRule,
  type RuleProblem,
} from '@shared/workflows'
import { api, ApiError, type WorkflowRuleInput, type WorkflowRuleView } from '../lib/api'
import { ReadOnlyNotice } from './ReadOnlyNotice'

const PRIORITY_TONE: Record<string, 'good' | 'warn' | 'bad' | undefined> = {
  urgent: 'bad',
  high: 'warn',
  normal: undefined,
  low: undefined,
}

const SLA_PRESETS = [15, 30, 60, 120, 240, 480, 1440]

/**
 * The rules that decide what becomes a ticket.
 *
 * A routing rule is written once and trusted for months, and when it goes wrong it does so
 * silently — a rule below the catch-all never fires, and a whole category of message quietly
 * stops being ticketed. So this screen is built around two things the list alone cannot give
 * you: a running check for rules that can never match, and a tester that shows what a real
 * message would do before anyone relies on it.
 */
export function Workflows() {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const data = useQuery({ queryKey: ['workflow-rules'], queryFn: api.workflowRules, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['workflow-rules'] })

  const reorder = useMutation({ mutationFn: api.reorderWorkflowRules, onSuccess: refresh })
  const remove = useMutation({ mutationFn: api.deleteWorkflowRule, onSuccess: refresh })

  if (data.isPending) return <div className="cad-card" style={{ height: '240px' }} />
  if (data.isError) {
    const message = data.error instanceof ApiError ? data.error.message : 'Unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const { rules, categories, canManage } = data.data!

  // Recomputed here rather than only trusted from the server, so a problem introduced by an
  // edit shows the moment the list changes instead of after the next round trip.
  const problems = findRuleProblems(rules)
  // Only an unreachable rule earns the badge. A duplicate keyword is worth mentioning but the
  // rule still fires on its other keywords, and labelling that "never runs" would send
  // someone hunting for a fault that is not there.
  const isDead = (index: number) =>
    problems.some((p) => p.index === index && p.kind === 'unreachable')

  const move = (index: number, direction: -1 | 1) => {
    const next = [...rules]
    const target = index + direction
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    reorder.mutate(next.map((r) => r.id))
  }

  return (
    <>
      {!canManage && <ReadOnlyNotice what="routing rules" />}
      <header className="stack gap-1">
        <span className="cad-eyebrow">Routing</span>
        <div className="row between wrap gap-3" style={{ alignItems: 'center' }}>
          <h1 className="cad-title">Rules</h1>
          {canManage && !adding && (
            <button type="button" className="cad-btn" onClick={() => setAdding(true)}>
              New rule
            </button>
          )}
        </div>
        <p className="cad-muted" style={{ maxWidth: '64ch' }}>
          Every message is checked against these in order, and the first match decides the
          ticket: what it is called, how urgent it is, and how long someone has to reply.
        </p>
      </header>

      {problems.length > 0 && (
        <div className="cad-card" style={{ borderColor: 'var(--cad-bad)' }}>
          <div className="stack gap-2">
            <h2 className="cad-section-title">Worth fixing</h2>
            {problems.map((problem, i) => (
              <p
                key={`${problem.kind}-${problem.index}-${i}`}
                className="cad-note"
                style={{ borderColor: 'var(--cad-bad)' }}
              >
                <b>{rules[problem.index]?.name ?? 'This set of rules'}</b> — {problem.message}
              </p>
            ))}
          </div>
        </div>
      )}

      <Tester rules={rules} />

      {adding && canManage && (
        <Editor
          categories={categories}
          onCancel={() => setAdding(false)}
          onSaved={() => {
            setAdding(false)
            void refresh()
          }}
        />
      )}

      <section className="cad-card">
        <div className="stack gap-3">
          <h2 className="cad-section-title">In order</h2>
          {reorder.error && (
            <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
              {reorder.error instanceof Error ? reorder.error.message : 'That order did not save.'}
            </p>
          )}
          {/* Without this the card rendered its heading over nothing, while the tester above it
              answered every sample with a red "No rule matches this" — which reads as a fault
              rather than as "you have not written any rules yet". */}
          {rules.length === 0 && (
            <p className="cad-muted">
              No routing rules yet. Until you add one, every message lands in the inbox without
              opening a ticket — which is a valid way to work, but nothing will be categorised,
              prioritised, or given a reply target automatically.
            </p>
          )}
          <div className="stack gap-2">
            {rules.map((rule, index) => (
              <div key={rule.id}>
                <div
                  className="row between wrap gap-3"
                  style={{
                    alignItems: 'center',
                    padding: '12px 0',
                    borderTop: index === 0 ? 'none' : '1px solid var(--cad-line)',
                    opacity: rule.enabled ? 1 : 0.55,
                  }}
                >
                  <div className="stack gap-1" style={{ minWidth: '220px', flex: 1 }}>
                    <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                      <span className="cad-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {index + 1}
                      </span>
                      <b>{rule.name}</b>
                      {!rule.enabled && <span className="cad-pill">off</span>}
                      {isDead(index) && (
                        <span className="cad-pill" data-tone="bad">
                          never runs
                        </span>
                      )}
                    </div>
                    <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                      {rule.keywords.length === 0
                        ? 'Catches everything else'
                        : rule.keywords.join(' · ')}
                      {rule.appliesTo ? ` — ${rule.appliesTo} chats only` : ''}
                    </span>
                  </div>

                  <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                    <span className="cad-pill">{rule.category}</span>
                    <span className="cad-pill" data-tone={PRIORITY_TONE[rule.ticketPriority]}>
                      {rule.ticketPriority}
                    </span>
                    <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                      reply in {formatSla(rule.slaMinutes)}
                    </span>
                  </div>

                  {canManage && (
                    <div className="row gap-1">
                      {/* Buttons rather than drag: order is the most consequential thing here,
                          and a keyboard user should be able to change it too. */}
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="quiet"
                        aria-label={`Move ${rule.name} earlier`}
                        disabled={index === 0 || reorder.isPending}
                        onClick={() => move(index, -1)}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="quiet"
                        aria-label={`Move ${rule.name} later`}
                        disabled={index === rules.length - 1 || reorder.isPending}
                        onClick={() => move(index, 1)}
                      >
                        ↓
                      </button>
                      <button
                        type="button"
                        className="cad-btn"
                        data-variant="text"
                        onClick={() => {
                          // Without this, a failed delete on one rule kept showing under
                          // whichever other rule's editor was opened next — the mutation's
                          // error state outlives the row it happened on.
                          remove.reset()
                          setEditing(editing === rule.id ? null : rule.id)
                        }}
                      >
                        {editing === rule.id ? 'Close' : 'Edit'}
                      </button>
                    </div>
                  )}
                </div>

                {editing === rule.id && canManage && (
                  <Editor
                    rule={rule}
                    categories={categories}
                    onCancel={() => setEditing(null)}
                    onSaved={() => {
                      setEditing(null)
                      void refresh()
                    }}
                    onDelete={() => {
                      if (confirm(`Delete this workflow?

It stops running immediately and its configuration cannot be recovered.`)) remove.mutate(rule.id)
                    }}
                    deleteError={
                      remove.error instanceof Error ? remove.error.message : undefined
                    }
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

/**
 * Runs a sample message through the rules, in the browser.
 *
 * The same shared engine the server uses, so there is no round trip and no chance of the
 * preview disagreeing with what actually happens. The trace matters more than the verdict:
 * being told which rule won answers half the question, and "why not the one I expected" is
 * the half someone is usually asking.
 */
function Tester({ rules }: { rules: WorkflowRuleView[] }) {
  const [sample, setSample] = useState('')
  const [kind, setKind] = useState<'direct' | 'group'>('direct')
  const fieldId = useId()

  const result = useMemo(
    () => (sample.trim() ? explainMatch(rules, { body: sample, kind }) : null),
    [rules, sample, kind],
  )

  return (
    <section className="cad-card">
      <div className="stack gap-3">
        <h2 className="cad-section-title">Try a message</h2>
        <div className="field-grid">
          <div className="cad-field" style={{ gridColumn: '1 / -1' }}>
            <label htmlFor={fieldId}>What might a customer send?</label>
            <input
              id={fieldId}
              className="cad-input"
              value={sample}
              onChange={(e) => setSample(e.target.value)}
              placeholder="My order is delayed, where is it?"
            />
          </div>
          <div className="cad-field">
            <label htmlFor={`${fieldId}-kind`}>In a</label>
            <select
              id={`${fieldId}-kind`}
              className="cad-input"
              value={kind}
              onChange={(e) => setKind(e.target.value as 'direct' | 'group')}
            >
              <option value="direct">Direct chat</option>
              <option value="group">Group chat</option>
            </select>
          </div>
        </div>

        {result && (
          <div className="stack gap-3">
            {result.matched ? (
              <div className="stack gap-2">
                <div className="row gap-2 wrap" style={{ alignItems: 'center' }}>
                  <span className="cad-pill" data-tone="good">
                    {result.matched.name}
                  </span>
                  <span className="cad-muted">opens a</span>
                  <span className="cad-pill">{result.matched.category}</span>
                  <span className="cad-pill" data-tone={PRIORITY_TONE[result.matched.ticketPriority]}>
                    {result.matched.ticketPriority}
                  </span>
                  <span className="cad-muted">
                    ticket, reply due in {formatSla(result.matched.slaMinutes)}
                  </span>
                </div>
                {result.matchedKeyword && (
                  <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
                    Matched on “{result.matchedKeyword}”.
                  </span>
                )}
                {result.matched.autoTags.length > 0 && (
                  <div className="row gap-2 wrap">
                    {result.matched.autoTags.map((tag) => (
                      <span key={tag} className="cad-pill">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
                No rule matches this. It would arrive in the inbox without opening a ticket, so
                nothing would count it or chase a reply.
              </p>
            )}

            <details>
              <summary className="cad-muted" style={{ cursor: 'pointer' }}>
                What each rule did
              </summary>
              <div className="stack gap-1" style={{ marginTop: '8px' }}>
                {result.trace.map((step, i) => (
                  <div key={`${step.name}-${i}`} className="row gap-2" style={{ alignItems: 'baseline' }}>
                    <span
                      className="cad-pill"
                      data-tone={step.outcome === 'matched' ? 'good' : undefined}
                    >
                      {step.outcome === 'matched' ? 'matched' : 'passed over'}
                    </span>
                    <b style={{ minWidth: '160px' }}>{step.name}</b>
                    <span className="cad-muted">{step.detail}</span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>
    </section>
  )
}

function Editor({
  rule,
  categories,
  onCancel,
  onSaved,
  onDelete,
  deleteError,
}: {
  rule?: WorkflowRuleView
  categories: string[]
  onCancel: () => void
  onSaved: () => void
  onDelete?: () => void
  deleteError?: string
}) {
  const id = useId()
  const [name, setName] = useState(rule?.name ?? '')
  const [keywords, setKeywords] = useState((rule?.keywords ?? []).join(', '))
  const [appliesTo, setAppliesTo] = useState<string>(rule?.appliesTo ?? '')
  const [category, setCategory] = useState(rule?.category ?? categories[0] ?? 'General')
  const [priority, setPriority] = useState(rule?.ticketPriority ?? 'normal')
  const [sla, setSla] = useState(String(rule?.slaMinutes ?? 240))
  const [tags, setTags] = useState((rule?.autoTags ?? []).join(', '))
  const [enabled, setEnabled] = useState(rule?.enabled ?? true)

  const draft: WorkflowRuleInput = {
    name: name.trim(),
    keywords: normaliseKeywords(keywords.split(',')),
    appliesTo: appliesTo === 'direct' || appliesTo === 'group' ? appliesTo : null,
    category: category.trim(),
    ticketPriority: priority as WorkflowRuleInput['ticketPriority'],
    slaMinutes: Number(sla),
    autoTags: tags.split(',').map((t) => t.trim()).filter(Boolean),
    enabled,
  }

  const problems = validateRule(draft)

  const save = useMutation({
    mutationFn: () =>
      rule ? api.updateWorkflowRule(rule.id, draft) : api.createWorkflowRule(draft),
    onSuccess: onSaved,
  })

  return (
    <div
      className="stack gap-3"
      style={{
        borderTop: '1px solid var(--cad-line)',
        paddingTop: '16px',
        marginBottom: '16px',
      }}
    >
      <div className="field-grid">
        <div className="cad-field">
          <label htmlFor={`${id}-name`}>Name</label>
          <input
            id={`${id}-name`}
            className="cad-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Delivery problem"
          />
        </div>
        <div className="cad-field">
          <label htmlFor={`${id}-category`}>Ticket category</label>
          <input
            id={`${id}-category`}
            className="cad-input"
            list={`${id}-categories`}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
          <datalist id={`${id}-categories`}>
            {categories.map((entry) => (
              <option key={entry} value={entry} />
            ))}
          </datalist>
        </div>
        <div className="cad-field">
          <label htmlFor={`${id}-priority`}>Priority</label>
          <select
            id={`${id}-priority`}
            className="cad-input"
            value={priority}
            onChange={(e) => setPriority(e.target.value as WorkflowRuleInput['ticketPriority'])}
          >
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </div>
      </div>

      <div className="cad-field">
        <label htmlFor={`${id}-keywords`}>
          Keywords — separated by commas. Leave blank to catch everything.
        </label>
        <input
          id={`${id}-keywords`}
          className="cad-input"
          value={keywords}
          onChange={(e) => setKeywords(e.target.value)}
          placeholder="delayed, where is, not arrived"
        />
        <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
          Matched anywhere in the message, ignoring case.
        </span>
      </div>

      <div className="field-grid">
        <div className="cad-field">
          <label htmlFor={`${id}-sla`}>Reply within (minutes)</label>
          <input
            id={`${id}-sla`}
            className="cad-input"
            list={`${id}-slas`}
            type="number"
            min={1}
            value={sla}
            onChange={(e) => setSla(e.target.value)}
          />
          <datalist id={`${id}-slas`}>
            {SLA_PRESETS.map((minutes) => (
              <option key={minutes} value={minutes} label={formatSla(minutes)} />
            ))}
          </datalist>
          <span className="cad-muted" style={{ fontSize: 'var(--cad-text-base)' }}>
            {Number(sla) > 0 ? formatSla(Number(sla)) : ' '}
          </span>
        </div>
        <div className="cad-field">
          <label htmlFor={`${id}-applies`}>Applies to</label>
          <select
            id={`${id}-applies`}
            className="cad-input"
            value={appliesTo}
            onChange={(e) => setAppliesTo(e.target.value)}
          >
            <option value="">Every chat</option>
            <option value="direct">Direct chats only</option>
            <option value="group">Group chats only</option>
          </select>
        </div>
        <div className="cad-field">
          <label htmlFor={`${id}-tags`}>Labels to apply — optional</label>
          <input
            id={`${id}-tags`}
            className="cad-input"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="escalation"
          />
        </div>
      </div>

      <label className="row gap-2" style={{ alignItems: 'center', cursor: 'pointer' }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>This rule is running</span>
      </label>

      {problems.map((problem) => (
        <p key={problem} className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {problem}
        </p>
      ))}
      {save.error && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {save.error instanceof Error ? save.error.message : 'That did not save.'}
        </p>
      )}
      {deleteError && (
        <p className="cad-note" style={{ borderColor: 'var(--cad-bad)' }}>
          {deleteError}
        </p>
      )}

      <div className="row gap-2 wrap">
        <button
          type="button"
          className="cad-btn"
          disabled={problems.length > 0 || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : rule ? 'Save' : 'Add rule'}
        </button>
        <button type="button" className="cad-btn" data-variant="quiet" onClick={onCancel}>
          Cancel
        </button>
        {onDelete && (
          <button type="button" className="cad-btn" data-variant="text" onClick={onDelete}>
            Delete
          </button>
        )}
      </div>
    </div>
  )
}

export type { RuleProblem }
